<template>
  <div class="dashboard-shell">
    <AppTopbar />
    <main class="page-shell">
      <div class="container-readable stack-lg">
        
        <!-- Hero Banner העליון בעיצוב המותג -->
        <section class="hero-banner">
          <div class="hero-container">
            <div class="hero-content">
              <router-link to="/" class="back-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                חזרה לאזור האישי
              </router-link>

              <h1 class="hero-title">שינוי רשת</h1>
              <p class="hero-subtitle">בחרו את הרשת המועדפת והטובה ביותר עבור המספר הפעיל שלכם.</p>
            </div>
          </div>
        </section>

        <!-- תוכן ראשי: בחירת הרשת -->
        <section class="surface card-block stack-md network-panel">
          <div class="panel-header">
            <h2 class="section-title">רשתות זמינות</h2>
            <p class="muted">לחצו על הרשת המבוקשת לעדכון מהיר</p>
          </div>

          <p v-if="loading" class="muted loading-text">טוען רשתות זמינות...</p>

          <div v-else-if="antennas.length > 0" class="antenna-grid">
            <button
              v-for="a in antennas" 
              :key="a.id" 
              type="button"
              class="antenna-card" 
              :class="{ selected: selectedId === a.id }"
              @click="selectedId = a.id"
            >
              <!-- אייקון אנטנה/קליטה -->
              <div class="antenna-icon-wrap">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12.55a11 11 0 0 1 14 0" />
                  <path d="M8.5 16.15a6 6 0 0 1 7 0" />
                  <circle cx="12" cy="20" r="1" fill="currentColor" />
                </svg>
              </div>

              <span class="antenna-name">{{ a.text }}</span>

              <!-- תגית לבחירה פעילה -->
              <div class="selection-badge" v-if="selectedId === a.id">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                נבחר
              </div>
            </button>
          </div>

          <p v-else class="muted">לא נמצאו רשתות זמינות.</p>

          <hr class="divider" />

          <!-- כפתורי פעולה והודעות -->
          <div class="actions-wrapper">
            <div class="actions-row">
              <button class="btn btn-primary" :disabled="!selectedId || saving" @click="confirm">
                <span v-if="!saving">עדכון רשת</span>
                <span v-else>שומר...</span>
              </button>
              <router-link to="/" class="btn btn-ghost">חזרה ללא שינוי</router-link>
            </div>

            <div v-if="error" class="error-inline">{{ error }}</div>
            <div v-if="successMsg" class="success-inline">{{ successMsg }}</div>
          </div>
        </section>

      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

export default {
  name: 'ChangeNetwork',
  components: { AppTopbar, AppFooter },
  data() {
    return {
      loading: false,
      antennas: [],
      selectedId: null,
      saving: false,
      error: '',
      successMsg: '',
      numberIndex: parseInt(localStorage.getItem('selected_number_index') || '0', 10),
    }
  },
  mounted() {
    if (this.isNull(localStorage.getItem('user_token'))) { this.$router.push('/login'); return }
    this.fetchAntennas()
  },
  methods: {
    fetchAntennas() {
      this.loading = true
      this.api({ action: 'get_antennas', method: 'get' }, (response) => {
        this.antennas = response?.data?.antennas || []
        this.loading = false
      }, () => { this.loading = false })
    },
    confirm() {
      if (!this.selectedId) return
      this.saving = true; this.error = ''; this.successMsg = ''
      this.api({
        action: 'change_my_antenna',
        data: { antenna_id: this.selectedId, number_index: this.numberIndex },
        method: 'post'
      }, (response) => {
        if (response?.data?.success) {
          const chosen = this.antennas.find(a => a.id === this.selectedId)
          if (chosen) localStorage.setItem('current_antenna', JSON.stringify({ id: chosen.id, text: chosen.text }))
          this.$router.push({ path: '/', query: { success: encodeURIComponent('הרשת עודכנה בהצלחה — ' + (chosen?.text || '')) } })
        } else this.error = 'לא הצלחנו לעדכן את הרשת. נסו שוב.'
        this.saving = false
      }, () => { this.error = 'שגיאה בעדכון הרשת.'; this.saving = false })
    },
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/tokens" as *;

// --- הבאנר העליון (Hero) ---
.hero-banner {
  background: linear-gradient(135deg, $color-brand 0%, lighten($color-brand, 10%) 100%);
  color: #ffffff;
  border-radius: $radius-lg;
  padding: $space-5 $space-6;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

.hero-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  max-width: 600px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.88rem;
  margin-bottom: $space-1;
  transition: opacity $transition-fast;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  svg {
    transform: rotate(180deg);
  }
}

.hero-title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}

.hero-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 1rem;
}

// --- פאנל הרשתות ---
.network-panel {
  border: 1px solid $color-line;
  border-radius: $radius-md;
  padding: $space-5;
  margin-top: $space-4;
}

.panel-header {
  margin-bottom: $space-3;

  .section-title {
    margin: 0 0 $space-1;
    font-size: 1.2rem;
    font-weight: 700;
  }
}

.loading-text {
  padding: $space-4 0;
}

// --- גריד הכרטיסיות ---
.antenna-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: $space-3;
}

.antenna-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $space-2;
  padding: $space-4 $space-3;
  background: #ffffff;
  border: 2px solid $color-line;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all $transition-fast;
  position: relative;
  min-height: 120px;

  &:hover {
    border-color: lighten($color-brand, 20%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);

    .antenna-icon-wrap {
      color: $color-brand;
      background: $color-brand-soft;
    }
  }

  &.selected {
    border-color: $color-brand;
    background: $color-brand-soft;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);

    .antenna-icon-wrap {
      background: $color-brand;
      color: #ffffff;
    }

    .antenna-name {
      color: $color-brand;
      font-weight: 700;
    }
  }
}

.antenna-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f4f0fa;
  color: #6c6283;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all $transition-fast;
}

.antenna-name {
  font-size: 1rem;
  font-weight: 600;
  color: $color-text;
  text-align: center;
}

.selection-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: $color-brand;
  background: #ffffff;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid $color-brand;
  margin-top: $space-1;
}

.divider {
  border: none;
  border-top: 1px solid $color-line;
  margin: $space-4 0;
}

// --- כפתורים והודעות ---
.actions-wrapper {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  max-width: 400px;
}

.actions-row {
  display: flex;
  gap: $space-3;

  .btn {
    flex: 1;
  }
}

.success-inline {
  border: 1px solid #b2f0c5;
  background: #f0fff6;
  color: #1e6b3a;
  border-radius: $radius-sm;
  padding: 0.65rem 0.85rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.error-inline {
  border: 1px solid #f0bfd0;
  background: #fff4f8;
  color: $color-danger;
  border-radius: $radius-sm;
  padding: 0.65rem 0.85rem;
  font-size: 0.9rem;
  font-weight: 500;
}

// --- רספונסיביות ---
@media (max-width: 768px) {
  .hero-banner {
    padding: $space-4;
  }

  .network-panel {
    padding: $space-4;
  }
}

@media (max-width: 480px) {
  .antenna-grid {
    grid-template-columns: 1fr;
  }

  .actions-row {
    flex-direction: column;
  }
}
</style>